import { useMemo } from "react";
import { IHttpClient } from "../../../lib/http";
import { StringBoolean, StringBooleanMapping } from "../../behaviour/domain/behaviour.entity";
import { OccupancyEntity, OccupationType } from "../domain/Occupancy.entity";

export interface OccupancyDto {
  fltr_tipo_ocupacion: string;
  minedadnino: string | null;
  maxedadnino: string | null;
  babyMinAge: string | null;
  teenagerMaxAge: string | null;
  adultMaxAge: string | null;
  filterGuestsByAge: StringBoolean;
}

function toInt(val: string, number: number) {
  return parseInt(val) || number;
}

function toBoolean(prop: StringBoolean | boolean) {
  if (typeof prop == "boolean") {
    return prop;
  }
  return prop === "1";
}

function toStringBoolean(prop: boolean) {
  return StringBooleanMapping[prop ? "true" : "false"];
}

function dtoToEntity(dto: OccupancyDto): OccupancyEntity {
  return OccupancyEntity.of({
    occupationType: dto.fltr_tipo_ocupacion as OccupationType,
    minChildrenAge: dto.minedadnino ? toInt(dto.minedadnino, 0) : null,
    maxChildrenAge: dto.maxedadnino ? toInt(dto.maxedadnino, 0) : null,
    minBabiesAge: dto.babyMinAge ? toInt(dto.babyMinAge, 0) : null,
    maxTeensAge: dto.teenagerMaxAge ? toInt(dto.teenagerMaxAge, 0) : null,
    maxSeniorsAge: dto.adultMaxAge ? toInt(dto.adultMaxAge, 0) : null,
    filterByAge: toBoolean(dto.filterGuestsByAge),
  });
}

function entityToDto(entity: OccupancyEntity): OccupancyDto {
  return {
    fltr_tipo_ocupacion: entity.occupationType,
    minedadnino: entity.minChildrenAge !== null ? entity.minChildrenAge.toString() : null,
    maxedadnino: entity.maxChildrenAge !== null ? entity.maxChildrenAge.toString() : null,
    babyMinAge: entity.minBabiesAge !== null ? entity.minBabiesAge.toString() : null,
    teenagerMaxAge: entity.maxTeensAge !== null ? entity.maxTeensAge.toString() : null,
    adultMaxAge: entity.maxSeniorsAge !== null ? entity.maxSeniorsAge.toString() : null,
    filterGuestsByAge: toStringBoolean(entity.filterByAge),
  };
}

function buildUrlParams(): URLSearchParams {
  const paramProperties = [
    "fltr_tipo_ocupacion",
    "minedadnino",
    "maxedadnino",
    "babyMinAge",
    "teenagerMaxAge",
    "adultMaxAge",
    "filterGuestsByAge",
  ];
  const params = new URLSearchParams();
  paramProperties.forEach(property => params.append("prop", property));
  return params;
}

async function getOccupancyOptions(
  httpClient: IHttpClient,
  propertyTicker: string
): Promise<OccupancyEntity> {
  const params: URLSearchParams = buildUrlParams();
  const occupancyDto = await httpClient.get<OccupancyDto>(
    `/WitBookerAPI-war/webresources/endpoint/establishment/${propertyTicker}/configurations`,
    { params }
  );
  return dtoToEntity(occupancyDto);
}

async function updateOccupancyOptions(
  httpClient: IHttpClient,
  propertyTicker: string,
  entity: OccupancyEntity
): Promise<void> {
  await httpClient.post(
    `/WitBookerAPI-war/webresources/endpoint/establishment/${propertyTicker}/createOrUpdateConfigurations`,
    entityToDto(entity)
  );
}

export const useOccupancyApi = (httpClient: IHttpClient) => {
  return useMemo(
    () => ({
      getOccupancyOptions: (propertyTicker: string) =>
        getOccupancyOptions(httpClient, propertyTicker),
      updateOccupancyOptions: (propertyTicker: string, entity: OccupancyEntity) =>
        updateOccupancyOptions(httpClient, propertyTicker, entity),
    }),
    [httpClient]
  );
};
